import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  CButton,
  CCol,
  CImage,
  CForm,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CRow,
  CAlert,
  CSpinner,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilEnvelopeClosed, cilBriefcase } from "@coreui/icons";
import axios from "axios";
import swal from "sweetalert";
import bgimage from "../../assets/Images/logo_black.png";
import { BASE_URL } from "../../components/constant_api";

const ConfirmOrganisationInvite = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // ── Values from the reminder link ───────────────────────
  const [userId, setUserId] = useState("");
  const [organisationId, setOrganisationId] = useState("");
  const [roleId, setRoleId] = useState("");
  const [token, setToken] = useState("");
  const [roleName, setRoleName] = useState("");

  // ── Values from the logged-in user (localStorage) ───────
  const [emailAddress, setEmailAddress] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const [pageLoading, setPageLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  // =====================================================
  // READ QUERY PARAMS FROM EMAIL LINK + LOGGED-IN USER
  // FROM LOCALSTORAGE (no extra API call needed)
  // =====================================================
  useEffect(() => {
    const uid = searchParams.get("UserID") || "";
    const orgId = searchParams.get("OrganisationID") || "";
    const role = searchParams.get("RoleID") || "";
    const tkn = searchParams.get("token") || "";
    const emailParam = searchParams.get("email") || "";
    const roleNameParam = searchParams.get("roleName") || "";

    setUserId(uid);
    setOrganisationId(orgId);
    setRoleId(role);
    setToken(tkn);
    setRoleName(roleNameParam);

    if (!uid || !orgId || !tkn) {
      setShowError(true);
      setErrorMessage("Invalid or expired invitation link.");
      setPageLoading(false);
      return;
    }

    // ── Pull logged-in user's details from localStorage ──
    try {
      const storedUser = JSON.parse(
        localStorage.getItem("user") || "null"
      );

      if (storedUser) {
        setEmailAddress(storedUser.EmailAddress || emailParam || "");
        setFirstName(storedUser.FirstName || "");
        setLastName(storedUser.LastName || "");
      } else if (emailParam) {
        setEmailAddress(emailParam);
      }
    } catch (err) {
      console.error("Error reading logged-in user from localStorage:", err);
      if (emailParam) {
        setEmailAddress(emailParam);
      }
    }

    setPageLoading(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =====================================================
  // ACCEPT ORGANISATION INVITE
  // Reuses the same /user/approve route — no Password field
  // is sent, so the backend treats this as an
  // already-registered user joining another organisation.
  // =====================================================
  const handleAccept = async () => {
    setShowError(false);

    try {
      setLoading(true);

      const response = await axios.post(
        `${BASE_URL}/user/approve`,
        {
          UserID: userId,
          OrganisationID: organisationId,
          RoleID: roleId,
          token: token,
        }
      );

      setShowSuccess(true);

      swal(
        "Invitation Accepted!",
        response?.data?.message ||
        "You've successfully joined the organisation.",
        "success"
      ).then(() => {
        navigate("/login");
      });
    } catch (error) {
      console.error("Error accepting organisation invite:", error);

      setShowError(true);

      setErrorMessage(
        error?.response?.data?.message ||
        "Failed to accept invitation. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-light min-vh-100 d-flex flex-row align-items-center">
      <div className="lgnauth">

        {/* =====================================================
            FORM SECTION
        ====================================================== */}
        <div className="lgnform">
          <CRow className="justify-content-center">
            <CCol md={12}>

              <CForm className="row g-3" noValidate>

                {/* TITLE */}
                <h2 className="lgntxt">
                  Invitation to Join Organisation
                </h2>

                {/* ERROR ALERT */}
                {showError && (
                  <CAlert
                    color="warning"
                    dismissible
                    onClose={() => setShowError(false)}
                    className="fade-alert"
                  >
                    {errorMessage}
                  </CAlert>
                )}

                {/* SUCCESS ALERT */}
                {showSuccess && (
                  <CAlert
                    color="success"
                    className="fade-alert"
                  >
                    Invitation accepted successfully.
                  </CAlert>
                )}

                {pageLoading ? (
                  <div className="text-center py-5 page-loader-wrap">
                    <CSpinner
                      color="primary"
                      style={{
                        width: "3rem",
                        height: "3rem",
                      }}
                    />

                    <p className="mt-3 mb-0 text-muted">
                      Loading invitation details...
                    </p>
                  </div>
                ) : !showError ? (
                  <>

                    <p className="text-muted mb-2">
                      You've been invited to join a new organisation
                      with your existing account.
                    </p>

                    {/* =================================================
                        EMAIL (from logged-in user, read-only)
                    ================================================== */}
                    <CInputGroup className="mb-2 input-anim">
                      <div className="single-input-field">
                        <CInputGroupText>
                          <CIcon icon={cilEnvelopeClosed} />
                        </CInputGroupText>

                        <CFormInput
                          id="EmailAddress"
                          type="email"
                          placeholder="Email Address"
                          value={emailAddress}
                          disabled
                          readOnly
                        />
                      </div>
                    </CInputGroup>

                    {/* =================================================
                        ROLE (from link params, read-only)
                    ================================================== */}
                    {roleName && (
                      <CInputGroup className="mb-3 input-anim">
                        <div className="single-input-field">
                          <CInputGroupText>
                            <CIcon icon={cilBriefcase} />
                          </CInputGroupText>

                          <CFormInput
                            id="RoleName"
                            type="text"
                            placeholder="Role"
                            value={roleName}
                            disabled
                            readOnly
                          />
                        </div>
                      </CInputGroup>
                    )}

                    {/* =================================================
                        ACCEPT INVITATION BUTTON
                    ================================================== */}
                    <CButton
                      color="primary"
                      className="px-4 btnsign d-flex align-items-center justify-content-center gap-2"
                      type="button"
                      disabled={loading || showSuccess}
                      onClick={handleAccept}
                    >
                      {loading && (
                        <CSpinner size="sm" />
                      )}

                      {loading
                        ? "Joining..."
                        : "Join Organisation"}
                    </CButton>

                  </>
                ) : null}

              </CForm>

            </CCol>
          </CRow>
        </div>

        {/* =====================================================
            LOGO / WELCOME PANEL
        ====================================================== */}
        <div className="logobg">

          <h2 className="weltxt">
            Welcome To
          </h2>

          <CImage
            src={bgimage}
            height="auto"
            align="center"
            width={300}
            className="mainlgo"
          />

          <p className="qqtxt">
            Lorem Ipsum is simply dummy text of the
            printing and typesetting industry. Lorem
            Ipsum has been the industry's standard dummy
            text ever since the 1500s, when an unknown
            printer took a galley of type and scrambled
            it to make a type specimen book.
          </p>

        </div>

      </div>

      {/* =====================================================
          SCOPED STYLES
      ====================================================== */}
      <style>{`
        .single-input-field {
          position: relative;
          display: flex;
          align-items: center;
          width: 100%;
        }

        .input-anim .form-control {
          transition:
            box-shadow 0.2s ease,
            border-color 0.2s ease;
        }

        .input-anim .form-control:focus {
          box-shadow:
            0 0 0 0.2rem
            rgba(50, 31, 219, 0.15);
        }

        .page-loader-wrap {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100%;
          animation: fadeIn 0.3s ease-in;
        }

        .fade-alert {
          animation: fadeIn 0.3s ease-in;
        }

        .btnsign {
          transition:
            transform 0.15s ease,
            box-shadow 0.2s ease,
            opacity 0.2s ease;
        }

        .btnsign:not(:disabled):hover {
          transform: translateY(-1px);
          box-shadow:
            0 4px 12px
            rgba(50, 31, 219, 0.25);
        }

        .btnsign:not(:disabled):active {
          transform: translateY(0);
        }

        .btnsign:disabled {
          opacity: 0.75;
          cursor: not-allowed;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-4px);
          }

          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Responsive */
        @media (max-width: 767px) {
          .lgnform .row > .col-md-6 {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default ConfirmOrganisationInvite;